import {useMemo} from 'react';
import {isBrandPortal} from '@netx/core/utils';
import {parseActions} from '@netx/core-next/utils';
import {useCurrentCartActions} from '@netx/core-cart-current/hooks';
import {
	useAssetExpirationProvider,
	assetExpirationActionExpire,
	assetExpirationActionUnexpire,
} from '@netx/core-asset-expiration';
import {assetRepurposeActionRepurpose} from '@netx/core-asset-repurpose';
import {assetCustomThumbnailActionSet} from '@netx/core-asset-custom-thumbnail';
import {type IAsset} from '@netx/core-asset-next/schema';
import {
	cartAssetActionAdd,
	cartAssetActionRemove,
} from '@netx/core-cart-asset/actions';
import {assetProjectActionAdd} from '@netx/core-asset-project';
import {useConstCallback} from '@netx/core-components/hooks/useConstCallback';
import {searchVisualActionAsset} from '@netx/core-search-visual';
import {
	useSavedCarts,
	cartSavedActionRemoveAsset,
	cartSavedActionRemoveSelectedAssets,
} from '@netx/core-cart-saved';
import {
	assetActionCategorize,
	assetActionCheckin,
	assetActionCheckout,
	assetActionCreatePDF,
	assetActionDelete,
	assetActionDownload,
	assetActionLock,
	assetActionReimport,
	assetActionResync,
	assetActionShareMenu,
	assetActionUnlock,
	assetActionMakeNotVisible,
	assetActionMakeVisible,
} from '../actions';

export type UseAssetActionsArguments = {
	asset?: IAsset;
	order?: boolean;
	inlcudeCartActions?: boolean;
	savedCartId?: number;
	omit?: string[];
	pick?: string[];
};

const portalActions = [
	'asset-create-pdf',
	'asset-download',
	'asset-share-menu',
	'asset-share-link',
	'asset-share-embed-link',
	'asset-share-publish-brightcove',
	'asset-share-publish-youtube',
	'search-visual-asset',
	'cart-asset-add',
	'cart-asset-remove',
	'cart-saved-remove-asset',
	'cart-saved-remove-selected-assets',
];

export const useAssetActions = (props: UseAssetActionsArguments = {}) => {
	const assetExpiration = useAssetExpirationProvider();
	const {actions: cartActions, loading} = useCurrentCartActions({order: false});

	const {savedCarts} = useSavedCarts();

	const getActions = useConstCallback(
		({
			asset,
			inlcudeCartActions = false,
			savedCartId,
			omit,
			order = true,
			pick,
		}: UseAssetActionsArguments) => {
			if (!asset) return [];

			const savedCart =
				savedCartId && savedCarts.find((cart) => cart.id === savedCartId);
			const applyCartActions = inlcudeCartActions && !isBrandPortal();

			let actions = applyCartActions
				? [
						...cartActions.flat(),
						cartAssetActionRemove(asset, {
							group: 9999,
							order: 1,
						}),
						assetActionDelete(asset, {
							group: 10000,
							order: 2,
						}),
					]
				: [
						cartAssetActionAdd(asset),
						assetActionCategorize(asset),
						assetActionCheckin(asset),
						assetActionCheckout(asset),
						assetActionDownload(asset),
						assetActionShareMenu(asset),
						assetActionCreatePDF(asset),
						assetActionLock(asset),
						assetActionUnlock(asset),
						assetExpirationActionExpire(asset, {
							cb(asset) {
								assetExpiration.expire(asset.assetId);
							},
						}),
						assetExpirationActionUnexpire(asset, {
							cb(asset) {
								assetExpiration.unexpire(asset.assetId);
							},
						}),
						assetProjectActionAdd(asset),
						assetRepurposeActionRepurpose(asset),
						assetActionReimport(asset),
						assetActionResync(asset),
						searchVisualActionAsset(asset),
						assetCustomThumbnailActionSet(asset),
						assetActionMakeVisible(asset),
						assetActionMakeNotVisible(asset),
						cartAssetActionRemove(asset, {
							group: 2,
							order: 1,
						}),
						assetActionDelete(asset, {
							group: 3,
							order: 1,
						}),
					];

			if (savedCart) {
				actions.push(
					applyCartActions
						? cartSavedActionRemoveSelectedAssets(savedCart)
						: cartSavedActionRemoveAsset(savedCart, asset),
				);
			}

			if (isBrandPortal()) {
				actions = actions.filter((action) =>
					portalActions.includes(action.name),
				);
			}

			return parseActions({
				actions,
				order,
				omit,
				pick,
				args: [asset],
			});
		},
	);

	const actions = useMemo(() => getActions(props), [props]);

	return {
		actions,
		getActions,
		loading,
	};
};

export default useAssetActions;
